import { useEffect, useMemo, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { AUTH_TOKEN_CHANGE_EVENT, getAccessToken } from '../services/authTokenService';
import { logger } from '../utils/logger';

/**
 * Custom hook to handle authentication state and token parsing
 * Returns decoded user data from the access token
 */
export default function useAuth() {
    const [token, setToken] = useState(() => getAccessToken());

    useEffect(() => {
        const syncToken = (event) => {
            setToken(event?.detail?.token ?? getAccessToken());
        };
        const syncStoredToken = (event) => {
            if (event.key === 'accessToken') setToken(event.newValue || '');
        };

        window.addEventListener(AUTH_TOKEN_CHANGE_EVENT, syncToken);
        window.addEventListener('storage', syncStoredToken);

        return () => {
            window.removeEventListener(AUTH_TOKEN_CHANGE_EVENT, syncToken);
            window.removeEventListener('storage', syncStoredToken);
        };
    }, []);

    const decodedUser = useMemo(() => {
        if (!token) return null;

        try {
            const decoded = jwtDecode(token);
            // Check if token is expired
            if (decoded.exp * 1000 < Date.now()) {
                return null;
            }
            return decoded;
        } catch (error) {
            logger.warn('Failed to decode token:', error);
            return null;
        }
    }, [token]);

    const isAuthenticated = !!decodedUser;
    const userId = decodedUser?.id || null;

    return {
        isAuthenticated,
        userId,
        decodedUser,
        token,
    };
}
